import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class FragmentScrollService {
    private readonly isBrowser: boolean;
    private initialized = false;

    private offsetTop = 0;

    constructor(
        private readonly router: Router,
        private readonly zone: NgZone,
        @Inject(PLATFORM_ID) platformId: object
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    /** Llama 1 vez (AppComponent) */
    init(options?: { offsetTop?: number }) {
        if (!this.isBrowser || this.initialized) return;
        this.initialized = true;

        this.offsetTop = options?.offsetTop ?? 0;
    }

    /**
     * ✅ Método manual:
     * - Si pasas `id`, hace scroll a ese id.
     * - Si NO pasas `id`, lee el fragment de la URL actual (#loquesea) y scrollea.
     */
    checkAndScroll(id?: string, opts?: { timeoutMs?: number }) {
        if (!this.isBrowser) return;

        const target = this.normalizeId(id ?? this.getFragmentFromCurrentUrl());

        if (!target) return;

        const timeoutMs = opts?.timeoutMs ?? 5000;

        // Espera a que Angular termine el render + layout
        this.runAfterStable(() => {
            this.waitForElementThenScroll(target, timeoutMs);
        });
    }

    /** Si quieres llamarlo directo con un id */
    scrollToId(id: string, opts?: { timeoutMs?: number }) {
        this.checkAndScroll(id, opts);
    }

    // -------------------------
    // Internals
    // -------------------------

    private getFragmentFromCurrentUrl(): string {
        // usa URL final del router (más fiable que window.location en algunos casos)
        const url = this.router.url || '';
        const frag = url.includes('#')
            ? decodeURIComponent(url.split('#')[1] || '')
            : '';
        return frag;
    }

    private runAfterStable(fn: () => void) {
        const sub = this.zone.onStable.subscribe(() => {
            sub.unsubscribe();
            // 2 rAF para asegurar layout recalculado
            requestAnimationFrame(() => requestAnimationFrame(fn));
        });
    }

    private waitForElementThenScroll(id: string, timeoutMs: number) {
        // 1) intento inmediato
        if (this.tryScroll(id)) return;

        // 2) observar DOM (ideal con @defer / render async)
        const obs = new MutationObserver(() => {
            if (this.tryScroll(id)) obs.disconnect();
        });

        obs.observe(document.body, { childList: true, subtree: true });

        setTimeout(() => obs.disconnect(), timeoutMs);
    }

    private tryScroll(id: string): boolean {
        const el = document.getElementById(id);
        if (!el) return false;

        const scrollEl = (document.scrollingElement ||
            document.documentElement) as HTMLElement;

        const top =
            el.getBoundingClientRect().top +
            (scrollEl.scrollTop || window.scrollY || 0) -
            this.offsetTop;

        // 👇 dejar que cualquier “scroll automático” ocurra y luego lo pisas tú
        setTimeout(() => {
            scrollEl.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

            // reintentos por cambios de layout (imágenes, sliders, defer…)
            setTimeout(
                () =>
                    scrollEl.scrollTo({
                        top: Math.max(0, top),
                        behavior: 'smooth',
                    }),
                60
            );
            setTimeout(
                () =>
                    scrollEl.scrollTo({
                        top: Math.max(0, top),
                        behavior: 'smooth',
                    }),
                180
            );
            setTimeout(
                () =>
                    scrollEl.scrollTo({
                        top: Math.max(0, top),
                        behavior: 'smooth',
                    }),
                350
            );
        }, 0);

        return true;
    }

    private normalizeId(id: string) {
        return (id ?? '').toString().trim().replace(/^#/, '');
    }
}
